import { mString, removeHtmlTags } from '../../utility';

import { GAP } from './lib/constants';

const invoicePdfFooter = ({
  doc,
  endY = 0,
  headerConfigs,
  isAEOB,
  invoice = {},
  hasClaims: hasClaimsProp
}) => {
  const {
    tax = 0,
    prevDiscount = 0,
    charge,
    amount_paid = 0,
    ins_payment = 0,
    ins_adjustment = 0,
    adjustment = 0,
    totalAmount,
    total,
    finalDiscount,
    amountDue,
    qrCode,
    memo
  } = invoice;

  const hasClaims = hasClaimsProp ?? headerConfigs?.hasClaims;

  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  const marginX = GAP;
  const columnX = pageWidth - marginX;
  const lineWidth = pageWidth - marginX * 2;
  const lineHeight = GAP;
  const extraSpacing = GAP * 2; // Extra padding from bottom because of footer page like counter of page and date

  // Build data first
  const footerData = [
    ['BILLED', mString(isAEOB ? charge : totalAmount)],
    ...(isAEOB && hasClaims
      ? [
          ['INSURANCE COVERED', `${ins_payment > 0 ? '-' : ''}${mString(ins_payment)}`],
          ['INSURANCE ADJUSTED', `${ins_adjustment > 0 ? '-' : ''}${mString(ins_adjustment)}`]
        ]
      : []),
    amount_paid > 0 && ['Patient Paid', `-${mString(amount_paid)}`],
    prevDiscount > 0 && ['Discount', `-${mString(prevDiscount)}`],
    adjustment > 0 && ['ADJUSTMENT', `-${mString(adjustment)}`],
    tax > 0 && ['Tax', `-${mString(tax)}`]
  ].filter(Boolean);

  let memoHeight = 0;
  if (memo) {
    const memoDims = doc.getTextDimensions(removeHtmlTags(memo), { maxWidth: lineWidth - 5 });
    memoHeight += Math.round(memoDims.h) + 17; // 10 (line height) + 5 (padding) + 2 (margin)
  }

  const rowCount = footerData.length + 3;
  const totalHeight = rowCount * lineHeight + (extraSpacing + (qrCode ? 40 : 0) + memoHeight);

  // Compute Y
  let startY = pageHeight - totalHeight;

  // Set styles
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(20, 60, 80);

  // create page to place footer if there is no place  in current page
  if (endY > startY) {
    doc.addPage();
  }

  const addRow = (label, value, options = {}) => {
    doc.text(label, marginX, startY, options);
    doc.text(value, columnX, startY, { align: 'right', ...options });
    startY += lineHeight;
  };

  const addImage = (image, width, height) => {
    doc.addImage(image, 'PNG', 90, startY, width, height);
    startY += lineHeight;
  };

  if (memo) {
    doc.setFont('helvetica', 'bold');
    doc.text('Memo', marginX, startY);
    startY += lineHeight - 5;

    doc.setFont('helvetica', 'normal');
    doc.text(removeHtmlTags(memo), marginX, startY, { maxWidth: lineWidth - 5 });
    startY += memoHeight;
  }

  // Render rows
  footerData.forEach(([label, value]) => addRow(label, value));

  doc.setDrawColor(200);
  doc.line(marginX, startY, marginX + lineWidth, startY);
  startY += lineHeight;

  //Add extra rows
  doc.setFont('helvetica', 'bold');
  addRow('SUBTOTAL', mString(total));

  doc.setFont('helvetica', 'normal');
  addRow('ADDITIONAL DISCOUNT', mString(finalDiscount));

  startY += 2;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  addRow('AMOUNT DUE', mString(amountDue));

  if (qrCode) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Scan to Pay', 105, startY - 1, { align: 'center' });
    addImage(qrCode, 30, 30);
  }

  return startY;
};

const printPdfFooters = {
  invoice: invoicePdfFooter
};

export const generateFooter = ({ doc, endY, headerConfigs, footerConfigs }) => {
  const footerFn = printPdfFooters?.[footerConfigs?.customFooter];
  if (footerFn) {
    return footerFn({ doc, endY, headerConfigs, ...footerConfigs.footerProps });
  }

  return endY;
};
